import React, {useState, useMemo, useCallback} from "react";
import {useNavigate} from "react-router-dom";
import {MainContainer} from "../../components/ContentWrapper";
import {ListPageHeader} from "../../components/ListPageHeader/ListPageHeader";
import {SOWFormModal} from "./SOWFormModal";
import {IconPencil, IconTrash, IconBook} from "../../utils/icons";
import {DotLottleLoader, LoadMoreBtn, PageDivider} from "../../components/Loader";
import axiosInstant from "../../api";
import {
    getBrandSOWs,
    getBrandSOWsByBrand,
    updateBrandSOW,
} from "../../api/endpoints";
import {ConfirmationDialog} from "../../components/ConfirmationDialog/ConfirmationDialog";
import toast from "react-hot-toast";
import {ReactComponent as AddIcon} from "../../assets/svg/add-icon.svg";
import {useUser} from "../../hooks/useUser";
import {formatEntityId} from "../videoProduction/helper";
import {useCommonParams} from "../../hooks/useCommonParams";
import SortControls from "../../components/SortControls/SortControls";
import {sortData} from "../../utils/sortUtils";
import {usePagination} from "../../hooks/usePagination";

interface SOWItem {
    id: number;
    name: string;
    url: string;
    brand?: {id: number; name: string; logo?: string; rmn?: boolean};
    created_at?: string;
    package_id?: number | null;
    package_snapshot?: {
        display_name: string;
        sell_price: string | number;
        max_skus: number;
        term_months: number | null;
    } | null;
    start_date?: string | null;
    end_date?: string | null;
    package_usage?: {skus_used: number; project_count: number; order_count: number};
}

const formatMonthYear = (dateStr: string) =>
    new Date(`${dateStr}T00:00:00`).toLocaleDateString(undefined, {
        month: "short",
        year: "numeric",
    });

const PAGE_SIZE = 20;

const SOWPage: React.FC = () => {
    const {
        searchParams,
        setSearchParams,
        searchText,
        setSearchText,
        page,
        setPage,
        sortType,
        resolvedSortKey,
        setSortDirection,
    } = useCommonParams({sortKeyMap: {dateCreated: "created_at"}});

    const [showNewSOW, setShowNewSOW] = useState(false);
    const [editModalKey, setEditModalKey] = useState(0);
    const [deleteSOW, setDeleteSOW] = useState<SOWItem | null>(null);
    const {user} = useUser();
    const navigate = useNavigate();

    const fetchFn = useCallback(
        async (pageNum: number) => {
            try {
                const offset = (pageNum - 1) * PAGE_SIZE;
                const params = new URLSearchParams();
                params.set("limit", String(PAGE_SIZE));
                params.set("offset", String(offset));
                if (searchText) params.set("search", searchText);

                let endpoint: string;
                if (user?.brand?.[0]?.id) {
                    endpoint = getBrandSOWsByBrand(user.brand[0].id);
                } else {
                    endpoint = getBrandSOWs();
                }

                const response = await axiosInstant.get(`${endpoint}?${params.toString()}`);
                const {rows, count} = response.data;
                return {rows: rows ?? [], count: count ?? 0, error: null};
            } catch (e: any) {
                const msg = e?.response?.data?.error ?? "Failed to load SOWs";
                toast.error(msg);
                return {rows: [], count: 0, error: msg};
            }
        },
        [user?.brand, searchText],
    );

    const {
        items: sows,
        totalCount: count,
        loading,
        hasMore,
        canLoadPrevious,
        loadingPrevious,
        error,
        initLoad,
        loadMore,
        loadPrevious,
        topSentinelRef,
        editingItem: editingSOW,
        openEdit: openEditSOW,
        closeEdit: closeEditSOW,
    } = usePagination<SOWItem>({
        fetchFn,
        pageSize: PAGE_SIZE,
        deepLink: { page, setPage, searchParams, setSearchParams, ignoreParams: ["page", "edit"] },
        editDeepLink: { getId: (s) => s.id },
    });

    const handleSOWClick = (sow: SOWItem) => {
        navigate(`/video-production/orders?sowId=${sow.id}`);
    };

    const sortedSOWs = useMemo(() => {
        if (sortType.isAscending) {
            return sortData(sows, resolvedSortKey, true);
        }
        return sows;
    }, [sows, resolvedSortKey, sortType.isAscending]);

    const handleDelete = async () => {
        if (!deleteSOW) return;
        try {
            await axiosInstant.patch(updateBrandSOW(deleteSOW.id), {
                deactivated: true,
            });
            await initLoad(page);
            toast.success("SOW deleted successfully.");
        } catch (err: any) {
            const msg =
                err?.response?.data?.error ??
                err?.message ??
                "Failed to delete SOW";
            toast.error(msg);
        } finally {
            setDeleteSOW(null);
        }
    };

    return (
        <MainContainer>
            <ListPageHeader
                title="SOW"
                actionLabel="New SOW"
                onActionClick={() => setShowNewSOW(true)}
                searchValue={searchText}
                onSearchChange={setSearchText}
                searchInputId="search-sow"
                searchRightContent={
                    <SortControls
                        isAscending={sortType.isAscending}
                        onDirectionChange={setSortDirection}
                    />
                }
            />

            <PageDivider
                count={count}
                label="SOWs"
                className="mt-[24px] mb-[24px]"
                canLoadPrevious={canLoadPrevious}
                hasMore={hasMore}
                onLoadPrevious={loadPrevious}
            />

            {loading && sows.length === 0 && (
                <div className="flex justify-center py-12">
                    <DotLottleLoader fill={false} />
                </div>
            )}
            {error && <div className="py-4 text-down">{error}</div>}
            {!loading && !error && sortedSOWs.length === 0 && (
                <div className="flex flex-col items-center justify-center gap-6 mt-40 text-muted">
                    <IconBook size={64} stroke="currentColor" />
                    <div className="text-text-2 font-circular text-[14px] font-[450] leading-normal not-italic">
                        You have no active SOWs
                    </div>
                    <button
                        type="button"
                        onClick={() => setShowNewSOW(true)}
                        className="flex items-center gap-1 bg-accent text-base text-white py-3 px-5 lg:px-10 cursor-pointer rounded-xl focus:outline-none focus:shadow-outline"
                    >
                        <AddIcon className="mr-1" />
                        New SOW
                    </button>
                </div>
            )}
            {!loading && !error && sortedSOWs.length > 0 && (
                <>
                {canLoadPrevious && <div ref={topSentinelRef} style={{ height: 1 }} />}
                {loadingPrevious && (
                    <div className="flex justify-center py-2">
                        <DotLottleLoader fill={false} sx={{ width: 100, height: 100 }} />
                    </div>
                )}
                <div className="flex flex-col gap-3">
                    {sortedSOWs.map((sow) => (
                        <div
                            key={sow.id}
                            className="flex items-center justify-between p-4 bg-surface border border-surface-2 rounded-xl hover:border-border-token transition-colors cursor-pointer"
                            onClick={() => handleSOWClick(sow)}
                        >
                            <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                    <span className="text-[16px] font-medium text-text-2">
                                        {sow.name}
                                    </span>
                                    <div
                                        className="bg-accent text-white font-bold text-xs rounded-md hover:bg-[#735C38] cursor-pointer p-1 px-2 shrink-0 whitespace-nowrap"
                                        title="Copy ID"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            navigator.clipboard.writeText(formatEntityId(sow.brand?.name || sow.name, sow.id));
                                            toast.success("ID copied to clipboard");
                                        }}
                                    >
                                        {formatEntityId(sow.brand?.name || sow.name, sow.id)}
                                    </div>
                                </div>
                                {sow.url && (
                                    <a
                                        href={sow.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[14px] text-accent-text hover:underline truncate max-w-[400px]"
                                        onClick={(e) => e.stopPropagation()}
                                    >
                                        {sow.url}
                                    </a>
                                )}
                                {sow.brand?.name && (
                                    <span className="mt-1 inline-block w-fit text-text-2 text-[14px] font-[450] leading-normal bg-surface-2 rounded-full px-3 py-1">
                                        {sow.brand.name}
                                    </span>
                                )}
                                {sow.package_snapshot && (() => {
                                    const skusUsed = sow.package_usage?.skus_used ?? 0;
                                    const maxSkus = sow.package_snapshot.max_skus;
                                    const overCapacity = skusUsed > maxSkus;
                                    const pastTerm =
                                        !!sow.end_date &&
                                        new Date(`${sow.end_date}T00:00:00`) < new Date();
                                    const alert = overCapacity || pastTerm;
                                    return (
                                        <span
                                            className={`mt-1 inline-block w-fit text-[13px] font-[450] leading-normal rounded-full px-3 py-1 ${
                                                alert
                                                    ? "bg-down-bg text-down"
                                                    : "bg-tint text-accent-text"
                                            }`}
                                        >
                                            {sow.package_snapshot.display_name} · {skusUsed} of{" "}
                                            {maxSkus} SKUs used
                                            {sow.end_date
                                                ? ` · ${pastTerm ? "term ended" : "term ends"} ${formatMonthYear(sow.end_date)}`
                                                : ""}
                                            {overCapacity ? " · over capacity" : ""}
                                        </span>
                                    );
                                })()}
                            </div>
                            <div className="flex gap-2 items-center">
                                <button
                                    type="button"
                                    className="p-2 rounded-md cursor-pointer bg-surface text-muted border border-transparent hover:bg-surface-2 hover:border-muted"
                                    title="Edit SOW"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        openEditSOW(sow);
                                    }}
                                >
                                    <IconPencil stroke="currentColor" size={18} />
                                </button>
                                <button
                                    type="button"
                                    className="p-2 rounded-md cursor-pointer bg-surface text-muted border border-transparent hover:bg-surface-2 hover:border-muted"
                                    title="Delete SOW"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setDeleteSOW(sow);
                                    }}
                                >
                                    <IconTrash
                                        stroke="currentColor"
                                        width={18}
                                        height={18}
                                    />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
                </>
            )}

            {sortedSOWs.length > 0 && hasMore && (
                <LoadMoreBtn onClick={loadMore} loading={loading} />
            )}

            {showNewSOW && (
                <SOWFormModal
                    open={showNewSOW}
                    onClose={() => setShowNewSOW(false)}
                    onSuccess={() => initLoad(1)}
                    mode="create"
                />
            )}
            {editingSOW && (
                <SOWFormModal
                    key={`edit-sow-${editingSOW.id}-${editModalKey}`}
                    open
                    onClose={closeEditSOW}
                    onSuccess={() => initLoad(page)}
                    mode="edit"
                    sowId={editingSOW.id}
                    initialName={editingSOW.name}
                    initialUrl={editingSOW.url}
                    initialBrand={editingSOW.brand || null}
                    initialPackageId={editingSOW.package_id ?? null}
                    initialStartDate={editingSOW.start_date ?? null}
                    initialEndDate={editingSOW.end_date ?? null}
                />
            )}
            {deleteSOW && (
                <ConfirmationDialog
                    open
                    title="Delete SOW"
                    description={`Are you sure you want to delete "${deleteSOW.name}"?`}
                    confirmLabel="Delete"
                    onClose={() => setDeleteSOW(null)}
                    onConfirm={handleDelete}
                />
            )}
        </MainContainer>
    );
};

export default SOWPage;
